import { Component } from '@theme/component';

const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;
const STORAGE_KEY = 'varkala:last-pincode';

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} input
 * @property {HTMLElement} result
 * @property {HTMLButtonElement} submitButton
 */

/** @extends {Component<Refs>} */
export class PincodeChecker extends Component {
  requiredRefs = ['form', 'input', 'result', 'submitButton'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener('submit', this.#handleSubmit);

    const saved = this.#getSavedPincode();
    if (saved) this.refs.input.value = saved;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener('submit', this.#handleSubmit);
    this.#controller?.abort();
  }

  #getSavedPincode() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  /** @param {string} pincode */
  #savePincode(pincode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, pincode);
    } catch {
      // Private browsing / storage disabled — not essential, skip silently.
    }
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    event.preventDefault();
    const pincode = this.refs.input.value.trim();

    if (!PINCODE_PATTERN.test(pincode)) {
      this.#renderError('Enter a valid 6-digit pincode.');
      return;
    }

    this.#savePincode(pincode);
    this.#check(pincode);
  };

  /** @param {string} pincode */
  async #check(pincode) {
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return;

    this.#controller?.abort();
    this.#controller = new AbortController();

    this.#renderLoading();
    this.refs.submitButton.disabled = true;

    try {
      const url = new URL(endpoint);
      url.searchParams.set('pincode', pincode);
      const response = await fetch(url, { signal: this.#controller.signal });
      const data = await response.json();

      if (!response.ok) {
        this.#renderError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      if (!data.serviceable) {
        this.#renderNotServiceable();
        return;
      }

      this.#renderResult(data);
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      this.#renderError('Could not check delivery right now. Please try again.');
    } finally {
      this.refs.submitButton.disabled = false;
    }
  }

  #renderLoading() {
    this.refs.result.dataset.state = 'loading';
    this.refs.result.textContent = 'Checking…';
  }

  /** @param {string} message */
  #renderError(message) {
    this.refs.result.dataset.state = 'error';
    this.refs.result.textContent = message;
  }

  #renderNotServiceable() {
    this.refs.result.dataset.state = 'unavailable';
    this.refs.result.textContent = "Sorry, we currently don't deliver to this pincode.";
  }

  /**
   * @param {{ city?: string, state?: string, estimated_min_date: string,
   *   estimated_max_date: string, prepaid?: boolean, cod?: boolean }} data
   */
  #renderResult(data) {
    this.refs.result.dataset.state = 'success';
    this.refs.result.replaceChildren();

    const location = document.createElement('p');
    location.className = 'pincode-checker__location';
    const locationParts = [data.city, data.state].filter(Boolean).join(', ');
    location.textContent = locationParts ? `Delivering to ${locationParts}` : 'Delivery available';

    const estimate = document.createElement('p');
    estimate.className = 'pincode-checker__estimate';
    estimate.textContent = `Estimated delivery: ${data.estimated_min_date} – ${data.estimated_max_date}`;

    this.refs.result.append(location, estimate);

    const modes = [];
    if (data.prepaid) modes.push('Prepaid');
    if (data.cod) modes.push('Cash on Delivery');

    if (modes.length) {
      const payment = document.createElement('p');
      payment.className = 'pincode-checker__payment';
      payment.textContent = `${modes.join(' & ')} available`;
      this.refs.result.append(payment);
    }
  }
}

if (!customElements.get('pincode-checker')) {
  customElements.define('pincode-checker', PincodeChecker);
}
